import React, { useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../../../../context/LanguageContext';
import '../style/before-after-section.css';
import { initializeBeforeAfterSection } from '../script/before-after-section';
import { initializeTitleAnimation } from '../script/title-animation';
import lightsImage from '../../../../../assets/logo/lights/lights.webp';
import beforeImage from '../../../../../assets/logo/car_double_image/1.webp';
import afterImage from '../../../../../assets/logo/car_double_image/2.webp';
import { trackPhoneClick } from '../../../../../presentation/components/email/utils/trackingUtils';

const BeforeAfterSection: React.FC = () => {
    const { t, language } = useLanguage();
    const containerRef = useRef<HTMLElement>(null);
    const twentyTwentyRef = useRef<HTMLDivElement>(null);
    const twentyTwentyMobileRef = useRef<HTMLDivElement>(null);

    // Access translations - recalculate when language changes to ensure re-render
    const translation = useMemo(() => t as any, [t, language]);
    const beforeAfterTranslations = useMemo(() => translation.BEFORE_AFTER, [translation, language]);
    const buttonsTranslations = useMemo(() => translation.BUTTONS, [translation, language]);

    useEffect(() => {
        if (!containerRef.current) {
            return;
        }

        let timer: NodeJS.Timeout | null = null;
        let titleTimer: NodeJS.Timeout | null = null;
        let isMounted = true;
        
        const hasInitialized = sessionStorage.getItem('beforeafter-titles-initialized');
        
        // Initialize title animation
        titleTimer = setTimeout(() => {
            if (!hasInitialized) {
                initializeTitleAnimation();
                sessionStorage.setItem('beforeafter-titles-initialized', 'true');
            } else {
                // On language change, just make titles visible without animation
                const titles = document.querySelectorAll('.before-after .title-animation');
                titles.forEach((title: Element) => {
                    const el = title as HTMLElement;
                    el.style.opacity = '1';
                });
            }
            
            // Refresh ScrollTrigger
            if (typeof window !== 'undefined' && (window as any).ScrollTrigger) {
                (window as any).ScrollTrigger.refresh();
            }
        }, 100);

        // Initialize the before-after section with a delay to ensure DOM is ready
        // Initialize desktop version
        timer = setTimeout(() => {
            if (isMounted && containerRef.current && twentyTwentyRef.current) {
                initializeBeforeAfterSection(
                    containerRef.current,
                    twentyTwentyRef.current,
                    beforeAfterTranslations?.before ?? 'Before',
                    beforeAfterTranslations?.after ?? 'After'
                );
            }
            // Initialize mobile version if it exists
            if (isMounted && containerRef.current && twentyTwentyMobileRef.current) {
                initializeBeforeAfterSection(
                    containerRef.current,
                    twentyTwentyMobileRef.current,
                    beforeAfterTranslations?.before ?? 'Before',
                    beforeAfterTranslations?.after ?? 'After'
                );
            }
        }, 300);

        return () => {
            isMounted = false;
            if (timer) {
                clearTimeout(timer);
            }
            if (titleTimer) {
                clearTimeout(titleTimer);
            }
        };
    }, [language, beforeAfterTranslations]);

    return (
        <section className="before-after" ref={containerRef}>
            <div className="before-after__shape-1 float-bob-y">
                <img loading="lazy" src={lightsImage} alt="" />
            </div>
            <div className="container">
                <div className="row">
                    <div className="col-xl-6">
                        <div className="before-after__left">
                            <div className="before-and-after__img-box">
                                <div className="before-after__img">
                                    <div 
                                        className="before-after-twentytwenty" 
                                        id="wrinkle-before-after"
                                        ref={twentyTwentyRef}
                                    >
                                        <img src={beforeImage} alt={beforeAfterTranslations?.before ?? 'Before'} loading="eager" />
                                        <img src={afterImage} alt={beforeAfterTranslations?.after ?? 'After'} loading="eager" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-6">
                        <div className="before-after__right">
                            <div className="section-title text-left sec-title-animation animation-style2">
                                <div className="section-title__tagline-box">
                                    <div className="section-title__tagline-border">
                                        <div className="section-title__shape-1">
                                            <i className="section-title__circle"></i>
                                        </div>
                                    </div>
                                    <h6 className="section-title__tagline">
                                        {beforeAfterTranslations?.fromWornToWow ?? 'RIGOR TÉCNICO, RESULTADOS VISÍVEIS'}
                                    </h6>
                                    <div className="section-title__tagline-border">
                                        <div className="section-title__shape-2">
                                            <i className="section-title__circle"></i>
                                        </div>
                                    </div>
                                </div>
                                <h3 
                                    className="section-title__title title-animation"
                                    key={`beforeafter-title-${language}`}
                                    dangerouslySetInnerHTML={{
                                        __html: beforeAfterTranslations?.watchHowWeBringLife ?? 
                                            'Veja como o Roady devolve o brilho e a performance ao <span style="color: #ea2127;">seu carro</span>'
                                    }}
                                />
                            </div>
                            {/* Mobile-only image section - appears after title */}
                            <div className="before-after__mobile-image">
                                <div className="before-after__left">
                                    <div className="before-and-after__img-box">
                                        <div className="before-after__img">
                                            <div 
                                                className="before-after-twentytwenty" 
                                                id="wrinkle-before-after-mobile"
                                                ref={twentyTwentyMobileRef}
                                            >
                                                <img src={beforeImage} alt={beforeAfterTranslations?.before ?? 'Before'} loading="eager" />
                                                <img src={afterImage} alt={beforeAfterTranslations?.after ?? 'After'} loading="eager" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <p 
                                className="before-after__text"
                                dangerouslySetInnerHTML={{
                                    __html: beforeAfterTranslations?.realResultsSpeak ?? 
                                        'O restauro automóvel do Roady Leiria combina experiência técnica e equipamentos de precisão para recuperar o aspeto e o desempenho original do veículo.<br>Da carroçaria ao motor, cada intervenção é realizada com rigor profissional e atenção ao detalhe.<br><br>'
                                }}
                            />
                            <ul className="before-after__points">
                                <li>
                                    <div className="icon">
                                        <span className="icon-checked"></span>
                                    </div>
                                    <h4>{beforeAfterTranslations?.fullBodyRepair ?? 'Reparação e polimento de pintura e carroçaria'}</h4>
                                </li>
                                <li>
                                    <div className="icon">
                                        <span className="icon-checked"></span>
                                    </div>
                                    <h4>{beforeAfterTranslations?.dentRepaired ?? 'Detalhamento completo e renovação estética'}</h4>
                                </li>
                                <li>
                                    <div className="icon">
                                        <span className="icon-checked"></span>
                                    </div>
                                    <h4>{beforeAfterTranslations?.engineRepair ?? 'Restauro técnico de componentes e motor'}</h4>
                                </li>
                            </ul>
                            <div className="before-after__btn-and-call-box">
                                <div className="before-after__btn-box">
                                    <Link to="/orcamento?service=headlight" className="thm-btn get-started-btn">
                                        {buttonsTranslations?.budget ?? 'Orçamento'}
                                        <span><i className="icon-next"></i></span>
                                    </Link>
                                </div>
                                <div className="before-after__call-box">
                                    <div className="icon">
                                        <span className="icon-phone-call"></span>
                                    </div>
                                    <div className="content">
                                        <p>{buttonsTranslations?.callUsNow || 'Fale Connosco'}</p>
                                        <h4>
                                            <a href="tel:+351244834503" onClick={() => trackPhoneClick('hero_homepage')}>
                                                (+351) 244 834 503
                                                <span 
                                                    className="info-icon phone-info-icon" 
                                                    data-tooltip="phoneCallInfo"
                                                    data-tooltip-text={language === 'pt' ? 'Chamada para a rede fixa nacional' : 'Call to national landline'}
                                                >
                                            <i className="fas fa-info-circle"></i>
                                                </span>
                                            </a>
                                        </h4>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default BeforeAfterSection;

